import React, { useEffect, useState } from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Checkbox,
  Chip,
  ContentTemplate,
  SectionHeader
} from 'web-library';
import { candidateService, KeyValueResponse } from '../../../../api';
import RegistrationExamLevel from './RegistrationExamLevel';
import DownloadAuditProgressModal from '../../../../components/modals/DownloadAuditProgressModal';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import { useNonNationalReport } from '../../logic/useNonNationalReport';
import LoadingComponent from '../../../../components/LoadingComponent';

const useStyles = makeStyles()((theme) =>
({
  warningText: {
    width: '100%',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  formElementContainer: {
    marginBottom: theme.spacing(4),
  },
  dateContainer: {
    width: '30%',
    marginRight: '5%',
    display: 'inline-block',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    '&:last-child': {
      [theme.breakpoints.down('md')]: {
        marginTop: theme.spacing(2),
      },
    },
  },
  errorContainer: {
    display: 'flex',
  },
  errMsg: {
    marginTop: '0 !important',
  },
  label: {
    color: theme.palette.text.secondary,
  },
  submitButtonContainer: {
    width: '100%',
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: '120px',
  },
  menuItemsContainer: {
    width: '65%',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  chipsContainer: {
    overflowX: 'hidden',
    width: '100%',
  },
  iconButton: {
    position: 'absolute',
    right: '45px',
    top: 'calc(50% - 12px)',
    padding: 0,
  },
  chipLabel: {
    padding: 0,
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 700,
    position: 'relative',
    transform: 'none!important',
  },
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(6),
    padding: '0 !important',
  },
  fromControlContainer: {
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  radioContainer: {},
}));

const EXAM_LEVEL_PLACEHOLDER = 'Select Exam Level';
const ACADEMIC_LEVEL_PLACEHOLDER = 'Select Academic Level';

const NonNationalReportDetails = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [searchTouched, setSearchTouched] = useState(false);
  const {
    data: nonNationalExamList,
    isLoading,
  } = candidateService.registrationReport.opadmin.useGetNonNationalExamList();

  const {
    form,
    submitGenerate,
    fileDownloadStatus,
    onDialogClose,
    dialogState,
    process,
  } = useNonNationalReport();
  const { handleSubmit, control, formState: { errors }, watch, trigger, setValue } = form;

  const watchSubjectExamLevel = watch('examLevel');

  const watchAcademicLevel = watch('academicLevel');

  useEffect(() => {
    if ((watchSubjectExamLevel && searchTouched) || (watchAcademicLevel && searchTouched)) {
      trigger('subjectExamLevel');
      trigger('academicLevel');
    }
  }, [trigger, searchTouched, watchSubjectExamLevel, watchAcademicLevel]);

  const checkDateRangeHandler = (formValue: any) => {
    const academicLevelFormValue = formValue?.academicLevel.length
      ? formValue.academicLevel
      : nonNationalExamList?.academicLevel;

    submitGenerate({ ...formValue, academicLevel: academicLevelFormValue });
  };

  const onSearchClickedHandler = async () => {
    if (Object.keys(errors).length === 0 && errors.constructor === Object) {
      setSearchTouched(true);
    } else {
      setSearchTouched(false);
    }
  };

  const registrationExamLevelPlaceholder = (selectedExamLevel: any) =>
    selectedExamLevel?.length ? '' : EXAM_LEVEL_PLACEHOLDER;

  const academicLevelPlaceholder = (selectedAcademicLevel: any) =>
    selectedAcademicLevel?.length ? '' : ACADEMIC_LEVEL_PLACEHOLDER;

  return (
    <ContentTemplate type="full">
      <SectionHeader title={t('admin->>RegistrationReportDetails.Report Details')} />
      {nonNationalExamList ? (
        <form noValidate onSubmit={handleSubmit(checkDateRangeHandler)}>
          <Box className={classes.formElementContainer}>
            <RegistrationExamLevel
              loading={isLoading}
              label={t('admin->>RegistrationReportDetails.SubjectExamLevel')}
              data={nonNationalExamList?.examLevel || []}
              control={control}
              isError={!!errors?.examLevel}
              errorMsg={errors?.examLevel?.message?.toString()}
              valueTitle="examLevel"
              onSetValue={setValue}
              placeholder={registrationExamLevelPlaceholder(watchSubjectExamLevel)}
            />
            <ControlledAutocomplete<KeyValueResponse, true, false, undefined>
              loading={isLoading}
              id="inputSelect"
              name="academicLevel"
              control={control}
              label={t('admin->>RegistrationReportDetails.AcademicLevel')}
              options={nonNationalExamList?.academicLevel || []}
              className={classes.menuItemsContainer}
              classes={{
                root: classes.rootContainer,
              }}
              placeholder={academicLevelPlaceholder(watchAcademicLevel)}
              error={!!errors?.academicLevel}
              helperText={errors?.academicLevel?.message?.toString()}
              multiple
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
              isOptionEqualToValue={(option: KeyValueResponse, value: KeyValueResponse) =>
                option.code === value.code
              }

              renderOption={(_, option, state, __) => (
                  <>
                    <Checkbox checked={state.selected}/>
                    {option.description}
                  </>
              )}
              renderTags={(tagValue: any, getTagProps: any) =>
                tagValue.map((option: { description: string }, tagIndex: any) => (
                  <Chip
                    id="auto-complete-subject"
                    label={option.description}
                    {...getTagProps({ index: tagIndex })}
                  />
                ))
              }
              disableCloseOnSelect
              onChange={(event: any, value: any) => {
                setValue('academicLevel', value || []);
              }}
              defaultValue={[]}
            />
          </Box>

          <Box className={classes.submitButtonContainer}>
            <ApiButton status={process} type="submit" onClick={onSearchClickedHandler}>
              {t('admin->>RegistrationReportDetails.Generate Report')}
            </ApiButton>
          </Box>
        </form>
      ) : (
        <LoadingComponent isOpaque />
      )}

      <DownloadAuditProgressModal
        fileDownloadStatus={fileDownloadStatus}
        modalState={dialogState}
        onDialogClose={onDialogClose}
      />
    </ContentTemplate>
  );
};

export default NonNationalReportDetails;
